import { Box, Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { MprColumnConfigList } from './MprColumnConfigList';
import { MprKpiColumnSettingsForm } from './MprKpiColumnSettingsForm';
import { MprMetricsAndCustomFieldColumnsForm } from './MprMetricsAndCustomFieldColumnsForm';
import {
  useMultiReportingCustomFieldColumnsSchema,
  useMultiReportingMetricsColumnsSchema,
} from './MultiPeriodReporting.schemas';
import { useMprColumnConfigs } from './useMprColumnConfigs';

interface IMultiPeriodReportingSettingsProps {
  onClose: () => void;
}

export function MultiPeriodReportingSettings({ onClose }: IMultiPeriodReportingSettingsProps) {
  const { resetDrafts } = useMprColumnConfigs();
  const onCancel = useCallback(() => {
    resetDrafts();
    onClose();
  }, [onClose, resetDrafts]);

  return (
    <BasicDialog open onClose={onCancel} title={'Report Settings'} fullWidth maxWidth={'lg'}>
      <Box padding={'0 2.5rem 1.5rem'}>
        <FieldSettings />
        <Stack direction={'row'} justifyContent={'end'} alignItems={'center'} mt={'1.5rem'}>
          <Stack direction={'row'} gap={'1rem'}>
            <Button variant={'outlined'} color={'secondary'} onClick={onCancel} size={'medium'}>
              Cancel
            </Button>
            <SaveAndRefreshButton onClose={onClose} />
          </Stack>
        </Stack>
      </Box>
    </BasicDialog>
  );
}

function SaveAndRefreshButton({ onClose }: { onClose: () => void }) {
  const { saveConfigState } = useMprColumnConfigs();

  const onClick = useCallback(() => {
    saveConfigState();
    // report is refetched on column config change (see useMprReportData)
    onClose();
  }, [onClose, saveConfigState]);

  return (
    <Button variant={'contained'} color={'secondary'} onClick={onClick}>
      Save and Refresh
    </Button>
  );
}

function FieldSettings() {
  return (
    <>
      <Stack direction={'row'} alignItems={'center'} mb='0.25rem' justifyContent={'space-between'}>
        <Box>
          <Typography variant='body2' mb='0.2rem'>
            Add Columns
          </Typography>
        </Box>
      </Stack>
      <Box display={'grid'} gridTemplateColumns={'minmax(0, 3fr)  minmax(0, 5fr)'} gap={'2rem'}>
        <ColumnConfigForm />
        <MprColumnConfigList />
      </Box>
    </>
  );
}

function ColumnConfigForm() {
  const [columnType, setColumnType] = useState<'kpi' | 'metrics' | 'customField'>('kpi');
  const metricsColumnsSchema = useMultiReportingMetricsColumnsSchema();
  const customFieldColumnsSchema = useMultiReportingCustomFieldColumnsSchema();
  return (
    <Box position={'relative'}>
      <ToggleButtonGroup
        id='column-type'
        color='secondary'
        size='small'
        value={columnType}
        exclusive
        onChange={(e, newValue: typeof columnType | null) => {
          if (newValue == null) {
            return;
          }
          setColumnType(newValue);
        }}
        sx={{ mb: '0.5rem' }}
      >
        <ToggleButton value={'kpi'}>KPIs</ToggleButton>
        <ToggleButton value={'metrics'}>Metrics</ToggleButton>
        <ToggleButton value={'customField'}>Custom Fields</ToggleButton>
      </ToggleButtonGroup>
      {columnType === 'kpi' && <MprKpiColumnSettingsForm />}
      {columnType === 'metrics' && <MprMetricsAndCustomFieldColumnsForm schema={metricsColumnsSchema} />}
      {columnType === 'customField' && (
        <MprMetricsAndCustomFieldColumnsForm schema={customFieldColumnsSchema} />
      )}
    </Box>
  );
}
